import { EntityLinks } from '@griply/types';
import React from 'react';
import { ChevronLeft, Plus16 } from 'shared/assets/icons';
import { GoalItemCard } from 'shared/components/ui/goal-item-card';
import { Icon } from 'shared/components/ui/icon';
import { Colors, IconButton, Sizes } from 'shared/components/ui/icon-button';
import { Paths } from 'shared/routes/paths';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { replaceUrlParams } from 'shared/utils/replace-url-params';

import * as Styled from './category-list-item.style';
import { SideBarItem, SideBarItemGoal } from './types';

export type CategoryListItemProps = {
  item: SideBarItem;
  goals: Goal[];
  links: EntityLinks;
  lifeAreas: LifeArea[];
  isDragged: boolean;
  isDraggable: boolean;
  onDragStart: (e: React.DragEvent, item: SideBarItemGoal) => void;
  onDragEnd: () => void;
  toggleCollapse: (id: ID) => void;
  collapsedItems: ID[];
  onCategoryAdd?: (id: ID, anchor: HTMLElement) => void;
};

const CategoryListItemComponent: React.FC<CategoryListItemProps> = ({
  item,
  goals,
  links,
  lifeAreas,
  isDragged,
  isDraggable,
  onDragStart,
  onDragEnd,
  toggleCollapse,
  collapsedItems,
  onCategoryAdd,
}) => {
  const isCollapsed = !collapsedItems.includes(item.id);
  const onCollapse = () => toggleCollapse(item.id);

  if ('lifeArea' in item) {
    const _onCategoryAdd = (e: React.MouseEvent<HTMLButtonElement>) =>
      onCategoryAdd?.(item.id, e.currentTarget);

    return (
      <Styled.CategoryContainer>
        <Styled.CategoryRow>
          <Styled.CategoryLinkContainer
            to={replaceUrlParams(Paths.LifeAreaDetail, { id: item.id })}
          >
            <Styled.CategoryTitle>{item.lifeArea.name}</Styled.CategoryTitle>

            <Styled.ChevronContainer>
              <Icon icon={ChevronLeft} />
            </Styled.ChevronContainer>
          </Styled.CategoryLinkContainer>

          <Styled.CategoryButtonsContainer>
            {!!onCategoryAdd && (
              <Styled.CategoryCreateButtonContainer>
                <IconButton
                  size={Sizes.Small}
                  icon={Plus16}
                  color={Colors.Default}
                  onClick={_onCategoryAdd}
                />
              </Styled.CategoryCreateButtonContainer>
            )}
            {item.hasGoals && (
              <IconButton
                size={Sizes.Small}
                icon={ChevronLeft}
                rotation={isCollapsed ? -90 : 0}
                color={Colors.Default}
                onClick={onCollapse}
              />
            )}
          </Styled.CategoryButtonsContainer>
        </Styled.CategoryRow>
      </Styled.CategoryContainer>
    );
  }

  const goal = item.goal;
  const hasSubGoals = item.hasGoals;
  const _onCollapse = hasSubGoals ? onCollapse : undefined;
  const _onDragStart = (e: React.DragEvent) => onDragStart(e, item);

  return (
    <Styled.GoalListItem $isDragging={isDragged} $level={item.level}>
      <Styled.GoalListItemInner
        draggable={isDraggable}
        onDragStart={_onDragStart}
        onDragEnd={onDragEnd}
      >
        <GoalItemCard
          goal={goal}
          lifeAreas={lifeAreas}
          links={links}
          goals={goals}
          collapsed={isCollapsed}
          onCollapse={_onCollapse}
          deadlineOnly
          showTypeIcon
        />
      </Styled.GoalListItemInner>
    </Styled.GoalListItem>
  );
};

export const CategoryListItem = React.memo(CategoryListItemComponent);
